// Assembles a playground from the shared pieces and one library adapter.
//
// The page sets window.PLAYGROUND = { home, logo, monaco, models } and loads
// dist/<library>/main.js, which calls startPlayground( adapter ):
//
//   id                 storage namespace
//   title              library name in the top bar and status messages
//   scripts, typings   example scripts and the .d.ts for their imports
//   ifcopenshellTypes  also give Monaco the ifcopenshell package typings
//   viewport           mount a viewer pane (false: output on the right)
//   accept, hint       model file types; hint over the viewport
//   boot( ctx )        start the library. Call ctx.editorSafe() as soon as
//                      its classic (UMD) scripts have run.
//   open( choice )     open a model entry: { id, label, url, file, key }.
//                      Without it there is no model picker: scripts open
//                      their own.
//   isOpen( result )   is what open() returned still the open model?
//   describe( result ) a short note for the output, e.g. the schema
//   reset()            the Reset view button
//
// ctx = { els, status, out, models, showPick, editorSafe }

import { createEditor } from './editor.js';
import { bindPaneToggles, bindSplitters, createPickCard, createStatus, mount } from './layout.js';
import { createLibrary } from './library.js';
import { createModels } from './models.js';
import { createOutput } from './output.js';
import { createRunner } from './runner.js';
import { createStore } from './store.js';

/** Stands in for the model picker when scripts open their own models. */
const NO_MODELS = {
	render() {},
	enable() {},
	open() {},
	ensure: () => Promise.resolve( null ),
	pending: () => false,
	current: () => null,
};

export async function startPlayground( adapter ) {

	const config = window.PLAYGROUND;
	const store = createStore( `aeco.playground.${ adapter.id }.` );
	const els = mount( {
		title: adapter.title,
		home: config.home,
		logo: config.logo,
		viewport: adapter.viewport,
		accept: adapter.accept,
		hint: adapter.hint,
	} );
	document.title = `AECO.dev | ${ adapter.title } Playground`;

	const status = createStatus( els );
	const showPick = createPickCard( els );
	const out = createOutput( els.console );
	els.consoleClear.addEventListener( 'click', out.clear );
	bindSplitters( els );
	bindPaneToggles( els );

	let ready = false;
	let api = { monaco: null, editor: null };
	const library = createLibrary( { els, store, scripts: adapter.scripts } );
	let runner = null;
	const models = adapter.open ? createModels( {
		els,
		store,
		entries: config.models,
		accept: adapter.accept,
		adapter,
		status,
		out,
		beforeSwitch: () => {

			runner.stop();
			showPick( null );

		},
	} ) : NO_MODELS;
	runner = createRunner( { els, out, library, models, isReady: () => ready, editor: () => api } );

	els.reset?.addEventListener( 'click', () => {

		if ( ready ) adapter.reset?.();

	} );

	models.render();
	library.start();
	runner.setRunning( 'idle' );
	out.debug( 'Ctrl+Enter runs the script. Esc stops it.' );

	let editorReady = null;
	const editorSafe = () => {

		editorReady ??= createEditor( els.editor, {
			vs: config.monaco,
			typings: adapter.typings,
			ifcopenshellTypes: adapter.ifcopenshellTypes,
		} ).then( ( created ) => {

			api = created;
			api.editor.addCommand( api.monaco.KeyMod.CtrlCmd | api.monaco.KeyCode.Enter, runner.run );
			library.attach( api );

		} ).catch( ( error ) => {

			els.editor.textContent = '';
			out.error( `Editor failed to load: ${ error?.message ?? error }` );

		} );

	};

	try {

		status( `Starting ${ adapter.title }…` );
		await adapter.boot( { els, status, out, models, showPick, editorSafe } );
		ready = true;
		models.enable();
		runner.setRunning( 'idle' );
		out.debug( `${ adapter.title } is ready.` );
		// Pages with models say "Opening…" next and settle once it is open.
		status( `${ adapter.title } ready`, { settle: 1400 } );
		models.open();

	} catch ( error ) {

		console.error( error );
		const message = /webgpu/i.test( String( error?.message ) )
			? 'This browser has no WebGPU. Try a recent Chrome or Edge.'
			: `${ adapter.title } failed to start: ${ error?.message ?? error }`;
		status( message, { error: true } );
		out.error( message );

	}

	editorSafe();
	await editorReady;

}
